import React, {useState} from "react";
import {Box, Button, Card, DataTable, Icon, Image, InlineStack, Modal, Spinner, Text, TextField,} from "@shopify/polaris";
import {ChevronLeftIcon, ChevronRightIcon, LockFilledIcon} from "@shopify/polaris-icons";
import {Icon as StatsIcon} from "../../components/RecommendationStats/RecommendationStats";
import {ProductCell} from "../ProductCell/ProductCell";
import {RecommendationStatus} from "../RecommendationStatus/RecommendationStatus";
import {RecommendationActions} from "../RecommendationActions/RecommendationActions";
import {Recommendation} from "../../pages/RecommendationsPage/RecommendationsPage";

const PLACEHOLDER =
    "https://cdn.shopify.com/shopifycloud/web/assets/v1/bd3234c6090b17dd7c6751aa595817c8.svg";

type Props = {
    shop: string | null;
    loading: boolean;
    recommendations: Recommendation[];
    productMap: Record<number, any>;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    totalPages: number;
    searchValue: string;
    setSearchValue: React.Dispatch<React.SetStateAction<string>>;
    isFreePlan: boolean;
    onDelete?: (id: number) => void;
    onEdit?: (rec: Recommendation) => void;
    onToggle?: (id: number, enabled: boolean) => void;
    isBillingActive?: boolean;
};

export default function RecommendationTable(tableProps: Props) {

    const [activeImage, setActiveImage] = useState<string | null | any>(null);

    const rows = React.useMemo(
        () =>
            tableProps.recommendations.map((rec: Recommendation) => [
                <ProductCell
                    key={`prod-${rec.product_id}`}
                    id={rec.product_id}
                    product={tableProps.productMap[rec.product_id]}
                    placeholder={PLACEHOLDER}
                    onImageClick={setActiveImage}
                    shop={tableProps.shop}
                />,
                <ProductCell
                    key={`rec-${rec.recommended_product_id}`}
                    id={rec.recommended_product_id}
                    product={tableProps.productMap[rec.recommended_product_id]}
                    placeholder={PLACEHOLDER}
                    onImageClick={setActiveImage}
                    shop={tableProps.shop}
                />,
                rec.algo.charAt(0).toUpperCase() + rec.algo.slice(1).toLowerCase(),
                <RecommendationStatus enabled={rec.enabled}/>,
                <div style={{display: "flex", gap: 4, justifyContent: "center"}}>
                    <StatsIcon type={"eye"}/>
                    <Text as={"h4"}>{rec.impressions?.toLocaleString() || "0"}</Text>
                </div>,
                <div style={{display: "flex", gap: 4, justifyContent: "center"}}>
                    <StatsIcon type={"cursor"}/>
                    <Text as={"h4"}>{rec.clicks?.toLocaleString() || "0"}</Text>
                </div>,
                <div style={{display: "flex", gap: 4, justifyContent: "center"}}>
                    <>
                        {!tableProps.isBillingActive ? <Icon source={LockFilledIcon} />:
                            <>
                                <StatsIcon type={"check"}/>
                                <Text as={"h4"}>{rec.conversions?.toLocaleString() || "0"}</Text>
                            </>
                        }
                    </>
                </div>,
                <div style={{display: "flex", gap: 4, justifyContent: "center"}}>
                    <>
                        {!tableProps.isBillingActive ? <Icon source={LockFilledIcon} />:
                            <>
                                <StatsIcon type={"chart"}/>
                                <Text as={"h4"}>
                                    {rec.conversion_rate ? rec.conversion_rate + "%" : "0.00%"}
                                </Text>
                            </>
                        }
                    </>
                </div>,
                <RecommendationActions
                    enabled={rec.enabled == 1}
                    onToggle={() => tableProps.onToggle?.(rec.id, !(rec.enabled == 1))}
                    onDelete={() => tableProps.onDelete?.(rec.id)}
                    onEdit={() => tableProps.onEdit?.(rec)}
                />,
            ]),
        [tableProps.productMap, tableProps.shop, tableProps.isFreePlan, tableProps.isBillingActive, tableProps]
    );

    return (
        <div>

            {activeImage && (
                <Modal open onClose={() => setActiveImage(null)} title="Product Image" size="large">
                    <Modal.Section>
                        <Image alt="Product" source={activeImage} style={{maxWidth: '100%', display: 'block', margin: 'auto'}}/>
                    </Modal.Section>
                </Modal>
            )}

            <div style={{display: "flex", justifyContent: "space-between", marginBottom: 16}}>
                <div style={{width: 400}}>
                    <TextField
                        label="Search"
                        labelHidden
                        placeholder="Search product"
                        value={tableProps.searchValue}
                        onChange={tableProps.setSearchValue}
                        clearButton
                        onClearButtonClick={() => tableProps.setSearchValue("")}
                        autoComplete="off"
                    />
                </div>
                {tableProps.totalPages > 1 && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                        }}
                    >
                        <Button
                            icon={<Icon source={ChevronLeftIcon}/>}
                            disabled={tableProps.page <= 1}
                            onClick={() => tableProps.setPage((p: number) => Math.max(p - 1, 1))}
                        />
                        <Text as="p">Page</Text>
                        <select
                            value={tableProps.page}
                            onChange={(e) => tableProps.setPage(Number(e.target.value))}
                            style={{padding: "4px 8px", borderRadius: 4}}
                        >
                            {Array.from({length: tableProps.totalPages}, (_, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {i + 1}
                                </option>
                            ))}
                        </select>
                        <Text as="p">of {tableProps.totalPages}</Text>
                        <Button
                            icon={<Icon source={ChevronRightIcon}/>}
                            disabled={tableProps.page >= tableProps.totalPages}
                            onClick={() => tableProps.setPage((p: number) => p + 1)}
                        />
                    </div>
                )}
            </div>

            <div style={{marginBottom: 24}}>
                <Card>
                    {tableProps.loading ? (
                        <InlineStack align="center">
                            <Spinner size="large"/>
                        </InlineStack>
                    ) : rows.length > 0 ? (
                        <DataTable
                            columnContentTypes={[
                                "text",
                                "text",
                                "text",
                                "text",
                                "numeric",
                                "numeric",
                                "numeric",
                                "text",
                                "text",
                            ]}
                            headings={[
                                "Product",
                                "Recommended",
                                "Algorithm",
                                "Status",
                                "Impressions",
                                "Clicks",
                                "Conversions",
                                "Conv. Rate",
                                "Actions",
                            ]}
                            rows={rows}
                            verticalAlign="middle"
                        />
                    ) : (
                        <Box>No Recommendations</Box>
                    )}
                </Card>
            </div>
        </div>
    );
}
